
import { useState } from "react";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { apiFetch } from "@/lib/apiClient";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { SupportLane } from "@/lib/supportAgent";

type Vote = "up" | "down";

type OneAiMessageFeedbackProps = {
  messageId: string;
  replyContent: string;
  lane?: SupportLane;
  mode: "app" | "product";
};

export function OneAiMessageFeedback({
  messageId,
  replyContent,
  lane,
  mode,
}: OneAiMessageFeedbackProps) {
  const [vote, setVote] = useState<Vote | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function persist(nextVote: Vote, nextComment?: string) {
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch("/api/support-agent/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vote: nextVote,
          comment: nextComment?.trim() || undefined,
          messageId,
          replyExcerpt: replyContent.slice(0, 800),
          lane,
          mode,
        }),
      });
      if (!res.ok) {
        const payload = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(payload?.error || `Request failed (${res.status})`);
      }
      if (nextComment?.trim()) setNoteSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save feedback");
      throw e;
    } finally {
      setSaving(false);
    }
  }

  async function onVote(next: Vote) {
    if (saving) return;
    setVote(next);
    setNoteSaved(false);
    try {
      await persist(next);
    } catch {
      setVote(null);
    }
  }

  async function submitNote() {
    if (!vote || saving) return;
    try {
      await persist(vote, comment);
      setDialogOpen(false);
    } catch {
      /* error already set */
    }
  }

  return (
    <div className="mt-2 border-t border-gray-100 pt-1.5 dark:border-white/10">
      <div className="flex flex-wrap items-center gap-1">
        <button
          type="button"
          disabled={saving}
          aria-pressed={vote === "up"}
          aria-label="Thumbs up — this reply helped"
          onClick={() => void onVote("up")}
          className={`inline-flex size-7 items-center justify-center rounded-md transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#0b3d2e] disabled:opacity-40 ${
            vote === "up"
              ? "bg-emerald-100 text-[#0b3d2e] dark:bg-emerald-900/50 dark:text-emerald-200"
              : "text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-zinc-700 dark:hover:text-gray-200"
          }`}
        >
          <ThumbsUp className="size-3.5" aria-hidden />
        </button>
        <button
          type="button"
          disabled={saving}
          aria-pressed={vote === "down"}
          aria-label="Thumbs down — this reply missed"
          onClick={() => void onVote("down")}
          className={`inline-flex size-7 items-center justify-center rounded-md transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#0b3d2e] disabled:opacity-40 ${
            vote === "down"
              ? "bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300"
              : "text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-zinc-700 dark:hover:text-gray-200"
          }`}
        >
          <ThumbsDown className="size-3.5" aria-hidden />
        </button>

        {vote ? (
          <button
            type="button"
            className="ml-1 text-[11px] font-medium text-[#0b3d2e] underline-offset-2 hover:underline dark:text-emerald-300"
            onClick={() => setDialogOpen(true)}
          >
            {noteSaved ? "Edit feedback" : "Leave feedback"}
          </button>
        ) : null}
      </div>

      {vote && !noteSaved ? (
        <p className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">
          Thanks — optional: leave a short note on how this felt.
        </p>
      ) : null}
      {noteSaved ? (
        <p className="mt-1 text-[10px] text-emerald-700 dark:text-emerald-300">Feedback saved.</p>
      ) : null}
      {error ? (
        <p className="mt-1 text-[10px] text-red-600 dark:text-red-300" role="alert">
          {error}
        </p>
      ) : null}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-sm gap-3 rounded-2xl border-emerald-900/15 p-4 sm:rounded-2xl">
          <DialogHeader className="space-y-1 text-left">
            <DialogTitle className="text-base text-[#0b3d2e] dark:text-emerald-200">
              Feedback on One AI
            </DialogTitle>
            <DialogDescription className="text-xs leading-relaxed">
              You marked this reply as {vote === "up" ? "helpful" : "not helpful"}. Tell us what felt
              right or off — tone, question quality, length, or trust.
            </DialogDescription>
          </DialogHeader>
          <label className="sr-only" htmlFor={`one-ai-fb-${messageId}`}>
            Feedback note
          </label>
          <textarea
            id={`one-ai-fb-${messageId}`}
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={2000}
            placeholder="e.g. Too long / wrong first question / felt salesy / perfect confirm flow…"
            className="w-full resize-none rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0b3d2e] dark:border-white/10 dark:bg-zinc-900 dark:text-gray-100"
          />
          <DialogFooter className="gap-2 sm:justify-end">
            <button
              type="button"
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-white/15 dark:text-gray-200 dark:hover:bg-zinc-800"
              onClick={() => setDialogOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving || !comment.trim()}
              onClick={() => void submitNote()}
              className="rounded-lg bg-[#0b3d2e] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0f4d3a] disabled:opacity-40 dark:bg-emerald-800"
            >
              {saving ? "Saving…" : "Submit feedback"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
